import {
  type ComponentProps,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Loader } from '@opendoor/atoms/loader';
import type { MarketplaceBrowseQuery } from 'components/marketplace/browse/__generated__/marketplace-browse.zeus';
import { centsToDollars } from 'components/marketplace/helpers/money';
import type { MarketplaceFavoriteChangeHandler } from 'components/marketplace/helpers/useMarketplaceFavorite';
import { PaginationActions } from 'components/marketplace/map/PaginationActions';
import { ShowMore } from 'components/marketplace/map/ShowMore';
import { ListingCard, type PhotoLoadMode } from 'components/marketplace/property/SimilarListings';
import type { ListingCardData } from 'components/marketplace/property/types';
import type { LikedPropertyIds } from 'components/marketplace/types';

type MarketplacePropertyList = MarketplaceBrowseQuery['marketplace']['listings'];
type MarketplacePropertyListItem = MarketplacePropertyList['listings'][number];

const listingCardData = (
  listing: MarketplacePropertyListItem,
  likedPropertyIds?: LikedPropertyIds,
): ListingCardData => ({
  addressToken: listing.addressId,
  baths: listing.bathrooms,
  beds: listing.bedrooms,
  canSelfTour: listing.canSelfTour,
  city: listing.city,
  dwellingType: listing.dwellingType,
  fullAddress: listing.fullAddress,
  listPrice: centsToDollars(listing.listPriceCents),
  listingState: listing.listingState,
  livingSquareFootage: listing.totalLivingSquareFootage,
  neighborhood: listing.neighborhood,
  photos: listing.photoUrls,
  isLiked: likedPropertyIds == null ? null : likedPropertyIds.has(listing.addressId),
  latitude: listing.location?.latitude ?? null,
  longitude: listing.location?.longitude ?? null,
  slug: listing.slug,
  state: listing.state,
  street: listing.street,
});

/**
 * How the list offers the rest of the result set. `showMore` appends the next
 * page in place and leaves the address bar alone; `pages` swaps one page for
 * another behind a real per-page URL, so every page is linkable and crawlable.
 */
type ListingPaginationMode =
  | { kind: 'showMore' }
  | {
      kind: 'pages';
      /** One-based, matching the URL. */
      currentPage: number;
      totalPages: number;
      hrefForPage: (page: number) => string;
      onPageSelect: (page: number) => void;
    };

const SHOW_MORE_MODE: ListingPaginationMode = { kind: 'showMore' };

interface PropertyListProps {
  cardVariant?: ComponentProps<typeof ListingCard>['variant'];
  isLoading: boolean;
  isLoadingMore: boolean;
  handleOnSeeMore: () => void;
  listings?: MarketplacePropertyList;
  likedPropertyIds?: LikedPropertyIds;
  initialFavoritesPending?: boolean;
  onFavoriteChange?: MarketplaceFavoriteChangeHandler;
  firstPhotoLoadMode: PhotoLoadMode;
  pagination?: ListingPaginationMode;
  onListingHover?: (addressId: string | null) => void;
}

export const PropertyListRefreshOverlay = ({
  children,
  isRefreshing,
}: PropsWithChildren<{ isRefreshing: boolean }>) => {
  // React 18 drops `inert` when it is passed as a boolean JSX prop.
  const setResultContentRef = useCallback(
    (element: HTMLElement | null) => {
      element?.toggleAttribute('inert', isRefreshing);
    },
    [isRefreshing],
  );

  return (
    <Col className="relative w-full" aria-busy={isRefreshing || undefined}>
      <Col ref={setResultContentRef} className="w-full">
        {children}
      </Col>
      {isRefreshing && <Box aria-hidden className="absolute inset-0 z-2 bg-od-bg-primary/72" />}
    </Col>
  );
};

export const PropertyList = ({
  cardVariant,
  isLoading,
  isLoadingMore,
  handleOnSeeMore,
  listings,
  likedPropertyIds,
  initialFavoritesPending,
  onFavoriteChange,
  firstPhotoLoadMode,
  pagination = SHOW_MORE_MODE,
  onListingHover,
}: PropertyListProps) => {
  const hoveredAddressIdRef = useRef<string | null>(null);
  const focusedAddressIdRef = useRef<string | null>(null);
  const onListingHoverRef = useRef(onListingHover);

  useEffect(() => {
    onListingHoverRef.current = onListingHover;
    const addressIds = new Set(listings?.listings.map((listing) => listing.addressId));
    const hoveredCardRemoved =
      hoveredAddressIdRef.current !== null && !addressIds.has(hoveredAddressIdRef.current);
    const focusedCardRemoved =
      focusedAddressIdRef.current !== null && !addressIds.has(focusedAddressIdRef.current);
    if (hoveredCardRemoved) {
      hoveredAddressIdRef.current = null;
    }
    if (focusedCardRemoved) {
      focusedAddressIdRef.current = null;
    }
    if (hoveredCardRemoved || focusedCardRemoved) {
      onListingHover?.(focusedAddressIdRef.current ?? hoveredAddressIdRef.current);
    }
  }, [listings, onListingHover]);

  // Removal uses this render's callback; the ref keeps unmount cleanup stable across rerenders.
  useEffect(
    () => () => {
      if (hoveredAddressIdRef.current !== null || focusedAddressIdRef.current !== null) {
        hoveredAddressIdRef.current = null;
        focusedAddressIdRef.current = null;
        onListingHoverRef.current?.(null);
      }
    },
    [],
  );

  const propertyCards = useMemo(() => {
    return listings?.listings.map((listing, index) => {
      return (
        <Box
          key={listing.addressId}
          onMouseEnter={() => {
            hoveredAddressIdRef.current = listing.addressId;
            onListingHover?.(listing.addressId);
          }}
          onMouseLeave={() => {
            hoveredAddressIdRef.current = null;
            onListingHover?.(focusedAddressIdRef.current);
          }}
          onFocus={() => {
            focusedAddressIdRef.current = listing.addressId;
            onListingHover?.(listing.addressId);
          }}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              focusedAddressIdRef.current = null;
              onListingHover?.(hoveredAddressIdRef.current);
            }
          }}
        >
          <ListingCard
            listing={listingCardData(listing, likedPropertyIds)}
            displayBadge={listing.displayBadge}
            favoriteOrigin="GALLERY"
            initialFavoritePending={initialFavoritesPending}
            onFavoriteChange={onFavoriteChange}
            photoLoadMode={index === 0 ? firstPhotoLoadMode : 'deferred'}
            photoMode="gallery"
            variant={cardVariant}
          />
        </Box>
      );
    });
  }, [
    listings,
    cardVariant,
    likedPropertyIds,
    initialFavoritesPending,
    onFavoriteChange,
    firstPhotoLoadMode,
    onListingHover,
  ]);

  if (isLoading && listings == null) {
    return (
      <Row className="w-full items-center justify-center">
        <Loader aria-label="Loading homes" size="lg" />
      </Row>
    );
  }

  return (
    <>
      <Box className="grid grid-cols-1 gap-x-4 gap-y-6 lg:grid-cols-2">{propertyCards}</Box>
      {pagination.kind === 'showMore' ? (
        listings?.nextPage != null && (
          <ShowMore onShowMore={handleOnSeeMore} isLoading={isLoadingMore} />
        )
      ) : (
        <PaginationActions
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          hrefForPage={pagination.hrefForPage}
          onPageSelect={pagination.onPageSelect}
          // `isLoadingMore` tracks Show more's fetchNextPage; a page change runs
          // through runSearch, so this is the flag that is actually in flight.
          isLoading={isLoading}
        />
      )}
    </>
  );
};
